import { Select } from 'ts/interfaces/Generic';
import axios from 'axios';
import { AutocompleteDynamicModel } from 'ts/dynamic/classes/SearchDynamicModel';
import {
  ChangeFlexResponse,
  ChangeFlightResponse,
  ChangeHotelResponse,
  ChangeTicketResponse,
  ChangeTransferResponse,
  DynamicEventResponse,
  FlightRulesDisplay,
  PrebuildEvent,
} from 'ts/dynamic/interfaces/DynamicResponse';
import { DynamicBookResponse, Pax } from 'ts/dynamic/interfaces/DynamicBookResponse';
import { TransferParams } from 'ts/dynamic/interfaces/DynamicParams';
import { DynamicTransferUpsellRespose } from 'ts/dynamic/interfaces/DynamicTransferUpsell';
import { faChevronRight, faPlane } from '@fortawesome/free-solid-svg-icons';
import { Screenshot, checkAHAVI } from 'utils/utils';
import { GetIataCodeParams, GetIataCodeResponse } from 'ts/dynamic/interfaces/DynamicParams';
import { Credit2000PaymentInfo, SubmitScreenshotsParams } from 'ts/shared/interfaces/Generic';
import { RoomClass } from 'ts/hotel/interfaces/HotelResponse';
import { resolveChildrenOccupancy } from 'utils/hotel';

export const getSearchFilters = async (): Promise<{
  sportCategories: Select[];
}> => {
  try {
    const url = 'Dynamic/GetSearchFilters';
    const { data } = await axios.get(url);

    const sportCategories = data.sportCategories.map((category: string) => ({
      label: category,
      value: category,
    }));
    sportCategories.unshift({ label: 'כל', value: 'כל' });
    return { sportCategories };
  } catch (err) {
    return { sportCategories: [] };
  }
};

export const autocompleteDynamicEvents = async (
  model: AutocompleteDynamicModel,
): Promise<any | undefined> => {
  try {
    const url = 'Dynamic/AutocompleteDynamicEvents';
    const { data } = await axios.post(url, model);
    return data.tickets;
  } catch (err) {
    return undefined;
  }
};

export const searchAttraction = async (
  model: AutocompleteDynamicModel,
): Promise<any | undefined> => {
  try {
    const url = 'Dynamic/searchAttraction';
    const { data } = await axios.post(url, model);
    sessionStorage.setItem('searchAttractionCurrency', data.currency?.toLowerCase() || '');
    const resultTickets = data.tickets.map((item: any) => ({
      ...item,
      ticket: {
        ...item.ticket,
        categoryImg: data.imagesByCategory.find(
          (cat: any) => cat.categoryDesc === item.ticket.categoryDesc,
        )?.imageLink,
      },
    }));

    return {
      ...data,
      tickets: resultTickets,
    };
  } catch (err) {
    return undefined;
  }
};

export const autocompletePrebuildEvents = async (
  model: AutocompleteDynamicModel,
): Promise<PrebuildEvent[] | undefined> => {
  try {
    const url = 'Dynamic/SearchPrebuildEvents';
    const { data } = await axios.post(url, model);
    return data.events;
  } catch (err) {
    return undefined;
  }
};

export const searchPrebuild = async (
  recaptchaToken: string,
  itemNumber: number,
): Promise<DynamicEventResponse | undefined> => {
  try {
    const url = 'Dynamic/PrebuildPackage';
    const { data } = await axios.post(url, { recaptchaToken, itemNumber });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const prebuildToDynamic = async (
  recaptchaToken: string,
  itemNumber: number,
): Promise<{ hasErrors: boolean; searchToken: string } | undefined> => {
  try {
    const url = 'Dynamic/PrebuildToDynamicSearch';
    const { data } = await axios.post(url, { recaptchaToken, itemNumber });
    return data;
  } catch (err) {
    return undefined;
  }
};
export const search = async (
  model: any,
  controller: AbortController,
): Promise<DynamicEventResponse | undefined> => {
  try {
    const url = 'Dynamic/SearchDynamicEvents';
    const { data } = await axios.post(url, { ...model }, { signal: controller.signal });
    return data;
  } catch (err) {
    return undefined;
  }
};
export const getHotels = async (
  searchToken: string,
  sysToken: string,
  controller: AbortController,
): Promise<any | undefined> => {
  try {
    const url = 'Hotels/GetHotelsInMemory';
    const body = {
      searchToken,
      sysToken,
    };

    const data = (await axios.post(url, body, { signal: controller.signal })).data;

    return data;
  } catch (err) {
    return undefined;
  }
};

export const getFlights = async (
  searchToken: string,
  skip: number,
  controller: AbortController,
): Promise<any | undefined> => {
  try {
    const url = 'Flights/GetFlights';

    return (await axios.post(url, { searchToken, skip }, { signal: controller.signal })).data;
  } catch (err) {
    return undefined;
  }
};
export const searchByToken = async (
  recaptchaToken: string,
  searchToken: string,
  matrixConfig: number,
): Promise<DynamicEventResponse | undefined> => {
  try {
    const url = 'Dynamic/SearchDynamicEvents';
    const { data } = await axios.post(url, {
      recaptchaToken,
      searchToken,
      matrixConfig: { matrixConfig },
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const changeFlight = async (
  recaptchaToken: string,
  dynamicSearchToken: string,
  newFlightId: string,
): Promise<ChangeFlightResponse | undefined> => {
  try {
    const url = 'Dynamic/ChangeFlight';
    const { data } = await axios.post(url, {
      recaptchaToken,
      dynamicSearchToken,
      newFlightId,
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const ohsForChangeHotel = async (
  dynamicSearchToken: string,
  hotel: any,
  room?: RoomClass,
): Promise<any | undefined> => {
  try {
    const url = 'Dynamic/OhsForChangeHotel';
    const { data } = await axios.post(url, {
      dynamicSearchToken,
      hotel,
      room,
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const changeHotel = async (
  recaptchaToken: string,
  dynamicSearchToken: string,
  newHotel: any,
  newRoom: RoomClass,
): Promise<ChangeHotelResponse | undefined> => {
  try {
    const url = 'Dynamic/ChangeHotel';
    const { data } = await axios.post(url, {
      recaptchaToken,
      dynamicSearchToken,
      newHotel,
      newRoom,
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const changeTicket = async (
  recaptchaToken: string,
  dynamicSearchToken: string,
  newTiketId: number,
): Promise<ChangeTicketResponse | undefined> => {
  try {
    const url = 'Dynamic/ChangeTicket';
    const { data } = await axios.post(url, {
      recaptchaToken,
      dynamicSearchToken,
      newTiketId,
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const changeFlex = async (
  recaptchaToken: string,
  flexFlightId: string,
  dynamicSearchToken: string,
): Promise<ChangeFlexResponse | undefined> => {
  try {
    const url = 'Dynamic/SetFlex';
    const { data } = await axios.post(url, {
      recaptchaToken,
      dynamicSearchToken,
      flexFlightId,
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const changeTransfer = async (
  recaptchaToken: string,
  dynamicSearchToken: string,
  ukeyForward?: string,
  ukeyReturn?: string,
): Promise<ChangeTransferResponse | undefined> => {
  try {
    const url = 'Dynamic/ChangeTransfer';
    const { data } = await axios.post(url, {
      recaptchaToken,
      dynamicSearchToken,
      ukeyForward,
      ukeyReturn,
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const getDetails = async (
  recaptchaToken: string,
  dynamicSearchToken: string,
): Promise<DynamicBookResponse | undefined> => {
  try {
    const language = localStorage.getItem('language') || 'he';
    const url = 'Dynamic/Details';
    const { data } = await axios.post(url, {
      recaptchaToken,
      dynamicSearchToken,
      language,
    });
    return data;
  } catch (err) {
    return undefined;
  }
};

export const book = async (
  recaptchaToken: string,
  dynamicSearchToken: string,
  paxes: Pax[],
  creditCard: boolean,
  paymentSettings: Credit2000PaymentInfo,
  tripId: string,
  companyId: string,
  couponAmount: number,
): Promise<any> => {
  try {
    const screenshotsJson = sessionStorage.getItem('screenshotsDynamic');
    let screenshots: string[] = [];
    if (screenshotsJson) {
      const screenshotsArr: Screenshot[] = JSON.parse(screenshotsJson);
      screenshots = screenshotsArr.map((screenShots) => screenShots.data);
    }
    const language = 1;
    const url = 'Dynamic/Book';
    const { data } = await axios.post(url, {
      recaptchaToken,
      dynamicSearchToken,
      paxes,
      language,
      creditCard,
      paymentSettings,
      screenshots,
      tripId,
      companyId,
      couponAmount,
    });

    return data;
  } catch (err) {
    return undefined;
  }
};

export const getFlightRules = async (params: {
  searchToken: string;
  id: string;
}): Promise<FlightRulesDisplay[] | undefined> => {
  try {
    const url = 'Flights/RuleDisplay';

    return (await axios.post(url, params)).data.ruleDsiplay.flightRulesDisplay;
  } catch (err) {
    return undefined;
  }
};

export const upsellTransfer = async (
  body: TransferParams,
): Promise<DynamicTransferUpsellRespose | undefined> => {
  try {
    const url = 'Dynamic/UpsellSearchTransfer';
    const { data } = await axios.post(url, body);
    return data;
  } catch (err) {
    return undefined;
  }
};

export const getFlightSearch = async (term: string): Promise<Select[]> => {
  try {
    const url = 'Flights/Autocomplete';

    const { airports } = (await axios.post(url, undefined, { params: { term } })).data;

    return (
      airports?.map((option: any) => {
        let icon;
        if (option.isChild === 'true' || option.airCityLang.extCode_1 === 'True') {
          icon = {
            path: faChevronRight,
            className: 'select-custom__chevron',
          };
        } else {
          icon = {
            path: faPlane,
            className: 'select-custom__plane',
          };
        }

        const label = option.regionNames ? option.regionNames.displayName : option.descWWW;
        return {
          value: option.code,
          label,
          icon,
          className: checkAHAVI(label) ? 'select-custom__rtl' : 'select-custom__ltr',
          additinalValue: term,
        };
      }) || []
    );
  } catch (err) {
    return [];
  }
};

export const getHotelSearch = async (recaptchaToken: string, term: string): Promise<Select[]> => {
  try {
    const url = 'Utils/MultiDestinationAutocomplete';

    const { destinations, airports } = (await axios.post(url, { term, recaptchaToken })).data;

    const hotels = destinations.map((option: any) => ({
      value: option.external_dest_id,
      label: option.name_long,
      icon: {
        path: faChevronRight,
        className: 'select-custom__plane',
      },
      data: {
        lat: option.geo_latitude,
        lon: option.geo_longitude,
        code: option.iata_code,
        countryCode: option.country_code,
      },
    }));

    const aeroports = airports.map((option: any) => ({
      value: option.external_dest_id,
      label: option.name_long,
      icon: {
        path: faPlane,
        className: 'select-custom__plane',
      },
      data: {
        lat: option.geo_latitude,
        lon: option.geo_longitude,
        code: option.iata_code,
        countryCode: option.country_code,
      },
    }));

    return [...hotels, ...aeroports].sort((a, b) => a.label.localeCompare(b.label));
  } catch (err) {
    return [];
  }
};

export const getIataCode = async (
  body: GetIataCodeParams,
): Promise<GetIataCodeResponse | undefined> => {
  try {
    const url = '/Utils/MultiDestinationAutocomplete';

    const { data } = await axios.post(url, body);
    return data;
  } catch (err) {
    return undefined;
  }
};

export const checkPaymentStatus = async (transactionId: string): Promise<any | undefined> => {
  try {
    const url = 'Dynamic/CheckPaymentStatus';

    return (await axios.post(url, { transactionId })).data;
  } catch (err) {
    return undefined;
  }
};

export const submitScreenshots = async (params: SubmitScreenshotsParams): Promise<any> => {
  try {
    const url = 'Dynamic/SubmitScreenshots';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const getFlightAirlines = async (): Promise<Select[]> => {
  try {
    const url = 'Flights/GeStaticData';

    const response = (await axios.post(url)).data;

    const airlines = response.flightAirlines.map((airline: any) => ({
      label: airline.text,
      value: airline.value,
    }));

    return airlines;
  } catch (err) {
    return [];
  }
};

interface UpdatePaymentBlockParams {
  sourcePrice: number;
  packageCurrency: string;
  addPrice: number;
  searchToken?: string;
  lang: string;
}

export const updatePaymentBlock = async (params: UpdatePaymentBlockParams) => {
  try {
    const url = 'Utils/UpdatePaymentBlock';
    return (await axios.post(url, undefined, { params })).data;
  } catch (err) {
    return undefined;
  }
};

export const loadGoogleByDynamicProposal = async (dyn: any, lang: string, token: string) => {
  if (!dyn.hotel || !dyn.flight) return;

  const hotelRoom = dyn.hotel?.roomClasses?.[0]?.hotelRooms?.[0];
  const childrenOccupancy = resolveChildrenOccupancy(
    hotelRoom?.numCnn,
    hotelRoom?.pax?.childrenAges,
  );
  const data = {
    recaptchaToken: token,
    cleanHotelName: dyn.hotel.item.name,
    bpid: dyn.hotel.item.bpid,
    countryCode: dyn.hotel.addressInfo.country,
    lat: dyn.hotel.position.latitude,
    lon: dyn.hotel.position.longitude,
    cleanAddress: dyn.hotel.addressInfo.address,
    query: {
      q: `Hotel: ${dyn.hotel.name}. Address: ${dyn.hotel.addressInfo.address}, ${dyn.hotel.addressInfo.city}, ${dyn.hotel.addressInfo.country}`,
      gL: 'il',
      hl: lang,
      checkIn: dyn.hotel?.roomClasses?.[0]?.checkIn || '',
      checkOut: dyn.hotel?.roomClasses?.[0]?.checkOut || '',
      adults: hotelRoom?.numAdt || 0,
      ...childrenOccupancy,
      currency: dyn.currency?.toUpperCase() || 'USD',
    },
  };

  try {
    return (await axios.post('/Hotels/SearchSerpHotels', data)).data;
  } catch (err) {
    return undefined;
  }
};

interface HotelsTripadvisorParams {
  bpid: string;
  lang: string;
}
export const hotelsTripadvisor = async (
  params: HotelsTripadvisorParams,
): Promise<any | undefined> => {
  try {
    return (await axios.post('/Hotels/TripAdvisor', params)).data;
  } catch (err) {
    return undefined;
  }
};
